import { useTranslation } from "react-i18next";
import AppSearchBox from "../searchBox";
import { useGlobalSearchQuery } from "../../api/network/adminApiServices";
import { setListingQueryParams, setModalsData, TListingQueryParams, TModalsState } from "../../api/store/commonSlice";
import { useSelector } from "react-redux";
import { useDebouncedCallback } from "use-debounce";
import { useDispatch } from "react-redux";
import { useEffect, useRef, useState } from "react";
import { GlobalSearchResponseItem, GlobalSearchResponseTypes, GlobalSearchResultDisplayItem, GlobalSearchResultDisplayItemSources } from "../../api/types/GlobalSearch";
import { useNavigate } from "react-router-dom";
import { routeUrls } from "../../navigation/routeUrls";
import { TDashboardMenuItem, useAppMenus } from "../../navigation/dashboardSideMenu";

const GlobalSearchBox = () => {
  const { t } = useTranslation();
  const { t: tGlobalSearch } = useTranslation('translation', { keyPrefix: 'globalSearch' });
  const { t: tSideMenu } = useTranslation("translation", { keyPrefix: "dashboard.sidemenu" });
  const modalsState = useSelector((state: { commonReducer: { modals: TModalsState } }) => state.commonReducer.modals);
  const listingQueryParams: TListingQueryParams = useSelector((state: any) => state.commonReducer.listingQueryParams);
  const { globalSearch: globalSearchQueryParams } = listingQueryParams;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
    dispatch(setListingQueryParams({ ...listingQueryParams, globalSearch: { ...globalSearchQueryParams, search: value } }));
  }, 500);

  const {
    data: dataGlobalSearch,
    isFetching: isFetchingGlobalSearch,
  } = useGlobalSearchQuery(
    globalSearchQueryParams,
    {
      skip: !globalSearchQueryParams?.search,
    }
  )

  useEffect(() => {
    if (globalSearchQueryParams?.search && !isFetchingGlobalSearch) {
      dispatch(setModalsData({ ...modalsState, showGlobalSearchDropdown: true }))
    }
  }, [globalSearchQueryParams?.search, isFetchingGlobalSearch])

  const {
    dashboardMenuItems,
    dashboardAdminsMenuItems,
  } = useAppMenus();

  const searchRenderRef = useRef(0);

  const hideDropdown = () => {
    dispatch(setModalsData({ ...modalsState, showGlobalSearchDropdown: false }))
    dispatch(setListingQueryParams({ ...listingQueryParams, globalSearch: { ...globalSearchQueryParams, search: "" } }));
    searchRenderRef.current++; // to force re-render
  }

  const handleGlobalSearchItemClick = (item: GlobalSearchResponseItem) => {
    switch (item.search_type) {
      case GlobalSearchResponseTypes.HELP_ARTICLE:
        if(item.slug)
          navigate(`${routeUrls.dashboardChildren.help}/${item.slug}`);
        break;
      case GlobalSearchResponseTypes.REPORT:
        navigate(routeUrls.dashboardChildren.reportsChildren.select_a_type);
        break;
      case GlobalSearchResponseTypes.PAGE:
        if (item.uri)
          navigate(item?.uri);
        break;
      default:
        break;
    }
  }

  const [globalSearchResults, setGlobalSearchResults] = useState<GlobalSearchResultDisplayItem[]>([]);
  useEffect(() => {
    const results: GlobalSearchResultDisplayItem[] = [];

    /* Global Search Results */
    if (!!dataGlobalSearch && !isFetchingGlobalSearch) {
      results.push(...dataGlobalSearch.map(item => ({
        display_title_for_sorting: `${tGlobalSearch(item.search_type)} ${item.name}`,
        object: item,
        source: GlobalSearchResultDisplayItemSources.GLOBAL_SEARCH,
      })));
    }

    /* Dashboard Menu Search Results */
    if (!!dashboardMenuItems) {
      results.push(...dashboardMenuItems
        .filter(item => item.slug.includes(globalSearchQueryParams?.search))
        .map(item => ({
          display_title_for_sorting: `${tGlobalSearch('dashboard')} ${tSideMenu(item.slug)}`,
          object: item,
          source: GlobalSearchResultDisplayItemSources.DASHBOARD_MENU,
        })));
    }

    /* Admins Menu Search Results */
    if (!!dashboardAdminsMenuItems) {
      results.push(...dashboardAdminsMenuItems
        .filter(item => item.slug.includes(globalSearchQueryParams?.search))
        .map(item => ({
          display_title_for_sorting: `${tGlobalSearch('administration')} ${tSideMenu(`admins.${item.slug}`)}`,
          object: item,
          source: GlobalSearchResultDisplayItemSources.DASHBOARD_ADMINS_MENU,
        })));
    }

    /* Help Page */
    if ('help'.includes(globalSearchQueryParams?.search.toLowerCase())) {
      results.push({
        display_title_for_sorting: `${tGlobalSearch('help')}`,
        object: {
          id: 0,
          name: tGlobalSearch('help'),
          search_type: GlobalSearchResponseTypes.PAGE,
          uri: routeUrls.dashboardChildren.help,
        } as GlobalSearchResponseItem,
        source: GlobalSearchResultDisplayItemSources.PAGE,
      });
    }

    setGlobalSearchResults(results);
  }, [
    dataGlobalSearch,
    isFetchingGlobalSearch,
    globalSearchQueryParams?.search,
  ]);

  return (
    <div className="relative w-full">
      <AppSearchBox
        key={`search-${searchRenderRef.current}`}
        wrapperClassName={`w-full lg:w-64 max-w-full${modalsState.showGlobalSearchDropdown ? ' z-popup' : ''}`}
        placeholder={t("type_here_to_search")}
        initialValue={globalSearchQueryParams?.search}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          debouncedSetSearchKeyword(e.target.value)
        }
        onClear={() => {
          debouncedSetSearchKeyword("")
          dispatch(setModalsData({ ...modalsState, showGlobalSearchDropdown: false }))
        }}
        loading={isFetchingGlobalSearch}
      />
      {modalsState.showGlobalSearchDropdown && (
        <>
          <div className="fixed top-0 left-0 w-full h-screen bg-white/70 backdrop-blur-[1px] z-popup-overlay" onClick={hideDropdown}></div>
          <div className="absolute top-full left-0 bg-white rounded-lg flex flex-col px-3 py-3 mt-2 w-96 shadow-lg border border-black/10 z-popup">
            <ul className="flex flex-col gap-1 text-sm max-h-[calc(100vh-120px)] overflow-auto">

              {/* All Search Results */}
              {globalSearchResults
                ?.sort((a, b) => a.display_title_for_sorting.localeCompare(b.display_title_for_sorting))
                ?.map((item, index) => (
                  <li key={index} className="flex gap-0">
                    <button type="button"
                      className={`flex-grow text-left px-4 py-3 overflow-hidden whitespace-nowrap text-ellipsis hover:bg-accent-blue-paleO66text-left text-sm font-semibold text-sidemenu-text rounded-lg hover:bg-accent-blue-paleO66`}
                      onClick={() => {
                        hideDropdown();
                        if (item.object.hasOwnProperty('path')) {
                          navigate((item.object as TDashboardMenuItem).path);
                        } else {
                          handleGlobalSearchItemClick(item.object as GlobalSearchResponseItem);
                        }
                      }}
                    >
                      {(item.object as GlobalSearchResponseItem).search_type !== GlobalSearchResponseTypes.PAGE && (
                        <span className="text-field-label-valid">
                          {
                            item.object.hasOwnProperty('path')
                              ? item.source === GlobalSearchResultDisplayItemSources.DASHBOARD_MENU
                                ? tGlobalSearch('dashboard')
                                : tGlobalSearch('administration')
                              : tGlobalSearch((item.object as GlobalSearchResponseItem).search_type)
                          } &rsaquo;{" "}
                        </span>
                      )}
                      <span className="text-sidemenu-submenu-text-active">
                        {
                          item.object.hasOwnProperty('path')
                            ? item.source === GlobalSearchResultDisplayItemSources.DASHBOARD_MENU
                              ? tSideMenu((item.object as TDashboardMenuItem).slug)
                              : tSideMenu(`admins.${(item.object as TDashboardMenuItem).slug}`)
                            : (item.object as GlobalSearchResponseItem).name
                        }
                      </span>
                    </button>
                  </li>
                ))}

              {/* No Results Found */}
              {globalSearchResults?.length === 0 && (
                <li className="text-center text-sm text-heading-gray888 py-2">
                  {tGlobalSearch(isFetchingGlobalSearch ? 'searching' : 'no_results_found')}
                </li>
              )}
            </ul>
          </div>
        </>
      )}
    </div>
  )
}
export default GlobalSearchBox;